/**
 * BlobShadows — a dark disc on the floor under anything airborne.
 *
 * From a 55° top-down camera the blob shadow is the number one depth cue: it
 * is how a player knows a round is flying at chest height and not sliding on
 * the deck, and how a lobbed shell says where it will land (the blob grows and
 * darkens as the shell comes down). One `InstancedMesh` of a flat disc,
 * immediate-mode: callers `add()` every frame for every airborne thing, and
 * `publish()` at the end of the frame uploads the live set. Nothing persists,
 * nothing allocates.
 */

import * as THREE from 'three/webgpu';
import { uv, float, vec3, smoothstep, instancedBufferAttribute } from 'three/tsl';
import { DECAL_LIFT } from '../combat/decals.js';

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();

export class BlobShadows {
  constructor(capacity) {
    this.capacity = capacity;
    this.cap = capacity;
    this.n = 0;
    const geometry = new THREE.CircleGeometry(1, 20).rotateX(-Math.PI / 2);
    this.strength = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    this.strength.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('aShadow', this.strength);
    const mat = new THREE.MeshBasicNodeMaterial();
    mat.colorNode = vec3(0.02, 0.025, 0.05);
    const r = uv().sub(0.5).mul(2).length();
    // A soft-edged blob with a solid heart, scaled by the per-instance strength.
    mat.opacityNode = smoothstep(float(1.0), float(0.55), r).mul(instancedBufferAttribute(this.strength));
    mat.transparent = true;
    mat.depthWrite = false;
    mat.fog = false;
    this.mesh = new THREE.InstancedMesh(geometry, mat, capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.frustumCulled = false;
    // Under every other ground mark: a shadow is the floor darkening, not a mark on it.
    this.mesh.renderOrder = 2;
    this.mesh.name = 'blob-shadows';
    this.mesh.userData.noOutline = true;
    this.stats = { peak: 0, dropped: 0 };
  }

  attach(scene) {
    scene.add(this.mesh);
    return this;
  }

  setCap(n) {
    this.cap = Math.max(0, Math.min(this.capacity, Math.floor(n)));
  }

  /** One shadow this frame at (x, floorY, z): `radius` in units, `strength` 0..1. */
  add(x, floorY, z, radius, strength = 0.4) {
    if (this.n >= this.cap) {
      this.stats.dropped++;
      return;
    }
    const i = this.n++;
    _p.set(x, floorY + DECAL_LIFT * 0.5, z);
    _s.set(radius, 1, radius);
    _m.compose(_p, _q, _s);
    this.mesh.setMatrixAt(i, _m);
    this.strength.array[i] = strength;
  }

  /** End of frame: upload what was added, then forget it. */
  publish() {
    const n = this.n;
    if (n > this.stats.peak) this.stats.peak = n;
    this.mesh.count = n;
    this.mesh.visible = n > 0;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.strength.needsUpdate = true;
    }
    this.n = 0;
  }

  clear() {
    this.n = 0;
    this.publish();
  }

  dispose() {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
